// src/components/Chat/shared/ReplyPreview.tsx
// Barra de preview acima do input quando respondendo uma mensagem

import { motion } from "framer-motion";
import { X, Reply, FileText, Image, Music } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChatMessage } from "@/types/chat";

interface ReplyPreviewProps {
  replyTo: ChatMessage;
  onCancel: () => void;
}

export function ReplyPreview({ replyTo, onCancel }: ReplyPreviewProps) {
  const { t } = useTranslation();

  // Truncar conteúdo para preview
  const truncateContent = (content: string | null, maxLength = 100) => {
    if (!content) return "";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  // Verificar se tem arquivos
  const getFilePreview = () => {
    let files = replyTo.files;

    // Converter string para array se necessário
    if (typeof files === "string") {
      try {
        files = JSON.parse(files);
      } catch {
        return null;
      }
    }

    if (!Array.isArray(files) || files.length === 0) return null;

    const file = files[0] as any;
    const fileName =
      replyTo.file_names?.[0] ||
      (typeof file === "string"
        ? file.split("/").pop()
        : file?.displayName || file?.originalName);

    // Detectar tipo de arquivo
    const extension = fileName?.toLowerCase().split(".").pop() || "";
    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
    const isAudio = ["mp3", "wav", "ogg", "m4a", "webm"].includes(extension);

    return {
      name: fileName,
      isImage,
      isAudio,
      count: files.length,
    };
  };

  const filePreview = getFilePreview();
  const senderName = replyTo.sender?.name || "Usuário";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, height: 0 }}
      animate={{ opacity: 1, y: 0, height: "auto" }}
      exit={{ opacity: 0, y: 20, height: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="border-t border-gray-200 bg-gray-50 px-4 py-2"
    >
      <div className="flex items-start gap-3">
        {/* Ícone de Reply */}
        <div className="flex-shrink-0 mt-1">
          <Reply size={16} className="text-blue-500" />
        </div>

        {/* Conteúdo do Preview */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-blue-600 mb-0.5">
            {t("chatFeatures.reply.replyingTo")} {senderName}
          </p>

          {/* Preview da mensagem */}
          <div className="bg-white rounded-lg border-l-2 border-blue-500 pl-2 py-1 pr-2">
            {replyTo.content && (
              <p className="text-sm text-gray-600 truncate">
                {truncateContent(replyTo.content)}
              </p>
            )}

            {/* Preview de arquivo */}
            {filePreview && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
                {filePreview.isImage && <Image size={14} />}
                {filePreview.isAudio && <Music size={14} />}
                {!filePreview.isImage && !filePreview.isAudio && (
                  <FileText size={14} />
                )}
                <span className="truncate text-xs">
                  {filePreview.name}
                  {filePreview.count > 1 && ` +${filePreview.count - 1}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Botão Cancelar */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCancel}
          className="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
          title={t("chatFeatures.reply.cancel")}
        >
          <X size={18} />
        </motion.button>
      </div>
    </motion.div>
  );
}

export default ReplyPreview;
